// import './utilities/wdyr';
import { createRoot } from 'react-dom/client';
import './css/index.css';
import Home from './pages/TarlingsHome';
import {
  BrowserRouter as Router,
} from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CustomTheme from './css/MaterialTheme';
import { BreakpointProvider } from 'react-socks';
import Emitter, { DISABLE_INSTALLER, INSTALL } from './utilities/Emitter';
import Database from './utilities/database';

let notistackRef: SnackbarProvider | null = null;

type BeforeInstallPromptEvent = EventListenerOrEventListenerObject & {
  prompt: () => Promise<void>;
}

let enableInstall = false;
let installPrompt: BeforeInstallPromptEvent | null = null;
const installCallback = () => {
  if (!installPrompt) return;
  void installPrompt.prompt();
  disableInAppInstallPrompt();
};

// Handle Google Translate DOM manipulation errors
// When Google Translate modifies the DOM, it can cause React to lose sync with the real DOM
window.addEventListener('error', (event) => {
  const errorMessage = event.message || event.error?.message || '';
  // Suppress Google Translate related DOM errors
  if (
    errorMessage.includes('removeChild') ||
    errorMessage.includes('insertBefore') ||
    errorMessage.includes('The node to be removed is not a child of this node')
  ) {
    console.warn('Google Translate DOM manipulation detected - suppressing error:', errorMessage);
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Catch unhandled promise rejections related to DOM operations
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.message || event.reason || '';
  if (
    typeof reason === 'string' &&
    (reason.includes('removeChild') || reason.includes('insertBefore'))
  ) {
    console.warn('Google Translate DOM manipulation in promise - suppressing error:', reason);
    event.preventDefault();
    return false;
  }
});

// Monitor for Google Translate activity
// Google Translate adds <font> tags and modifies the DOM structure
let translationDetected = false;
const detectTranslation = () => {
  if (translationDetected) return;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Check if Google Translate added font tags or modified text nodes
      if (
        mutation.type === 'childList' &&
        mutation.addedNodes.length > 0
      ) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (
            node.nodeName === 'FONT' ||
            (node as Element).classList?.contains('translated-ltr') ||
            (node as Element).classList?.contains('translated-rtl')
          ) {
            if (!translationDetected) {
              translationDetected = true;
              console.warn('Google Translate activity detected. Some DOM errors may be suppressed to prevent crashes.');
            }
            return;
          }
        }
      }
    }
  });

  // Start observing when the DOM is ready
  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  } else {
    // If body isn't ready yet, wait for DOMContentLoaded
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    });
  }
};

// Start detection
detectTranslation();

window.addEventListener('vite:preloadError', () => {
  window.location.reload() // for example, refresh the page
})

window.addEventListener('beforeinstallprompt', (event: any) => {
  event.preventDefault();
  installPrompt = event;
  enableInstall = true;
  Emitter.emit(DISABLE_INSTALLER, false);
});

window.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // console.log('page has been hidden - cancel any pending save events');
    // maybe here we just reload it next time it's shown maybe after n minutes
  } else {
    // console.log('page has is now shown again - handle future events');
  }
  Database.performIntegrityCheck = true;
});

window.addEventListener('pagehide', () => {
  // console.log('pagehide called - cancel any pending save events');
  Database.performIntegrityCheck = true;
});

function disableInAppInstallPrompt() {
  installPrompt = null;
  Emitter.emit(DISABLE_INSTALLER, true);
}

Emitter.on(INSTALL, installCallback);

void navigator.serviceWorker.register('/serviceWorker.js');
const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={CustomTheme}>
      <Router>
        <SnackbarProvider
          maxSnack={5}
          preventDuplicate
          ref={(ref) => notistackRef = ref}>
          <BreakpointProvider>
            <Home notistackRef={() => notistackRef} enableInstaller={enableInstall} />
          </BreakpointProvider>
        </SnackbarProvider>
      </Router>
    </ThemeProvider >
  </StyledEngineProvider>);

